import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import React, { useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import Slide from "@mui/material/Slide";
import { MultipleContainers } from "./dnd/Multiple-Containers-Sortable/MultipleContainers";
import CustomSelect from "./CustomSelect";
import { CARD_BORDER } from "../utils/colors";
import { FS_BODY, FS_TITLE, FW_SEMIBOLD } from "../utils/typography";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// Fullscreen add/edit dialog for a module's sync setting.
// Header: module autocomplete + field search (filters BOTH lists) + close.
// Body: dnd dual list. Footer pinned: cadence select, 30-day delete
// checkbox, Save — the lists shrink, the footer never scrolls away.
// NOTE: keep the dialog at MUI's default z-index (1300); the drag overlay
// portal renders at 1400 above it.
const CustomDialog = ({
  open,
  mode,
  handleClose,
  modules,
  customAction,
  handleModuleChange,
  currentModuleFields,
  setCurrentModuleFields,
  currentDatabaseFields,
  setCurrentDatabaseFields,
  syncDuration,
  setSyncDuration,
  setDeleteBackupData,
  handleSave,
  selectedModule,
  deleteBackupData,
  handleDeleteField,
  syncAllFields,
  unSelectAllFields,
  syncDurations,
}) => {
  const [search, setSearch] = useState("");

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={handleClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      TransitionComponent={Transition}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          minHeight: 0,
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            px: 2.5,
            py: 1.5,
            borderBottom: CARD_BORDER,
          }}
        >
          {!customAction && (
            <Autocomplete
              id="modules-select"
              sx={{ width: 320 }}
              disabled={mode === "editMode"}
              size="small"
              options={modules}
              autoHighlight
              getOptionLabel={(option) =>
                option.plural_label || option.module_name
              }
              onChange={handleModuleChange}
              value={selectedModule}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Module"
                  placeholder="Select a module"
                  inputProps={{
                    ...params.inputProps,
                  }}
                />
              )}
            />
          )}
          {customAction && (
            <Typography sx={{ fontWeight: FW_SEMIBOLD, fontSize: FS_TITLE }}>
              {selectedModule?.plural_label ||
                selectedModule?.module_name ||
                "Module Sync"}
            </Typography>
          )}
          <TextField
            size="small"
            sx={{ flex: 1, maxWidth: 420 }}
            placeholder="Search fields…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <Box sx={{ flex: 1 }} />
          <IconButton onClick={handleClose} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Body — lists shrink, never push the footer away */}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: "auto",
            display: "flex",
            flexDirection: "column",
            p: 2.5,
          }}
        >
          <MultipleContainers
            currentModuleFields={currentModuleFields}
            setCurrentModuleFields={setCurrentModuleFields}
            currentDatabaseFields={currentDatabaseFields}
            setCurrentDatabaseFields={setCurrentDatabaseFields}
            handleDeleteField={handleDeleteField}
            syncAllFields={syncAllFields}
            unSelectAllFields={unSelectAllFields}
            externalSearchText={search}
          />
        </Box>

        {/* Footer — pinned */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 3,
            px: 2.5,
            py: 1.5,
            borderTop: CARD_BORDER,
            bgcolor: "#fafafa",
          }}
        >
          {!customAction && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "1rem",
              }}
            >
              <Typography sx={{ fontSize: FS_BODY }}>
                Sync Frequently:
              </Typography>

              <CustomSelect
                selectedValue={syncDuration}
                items={syncDurations}
                setValue={(selectedValue) => {
                  const convertIntoHours = selectedValue / 60;
                  const label = Number.isInteger(convertIntoHours)
                    ? `${convertIntoHours} Hours`
                    : `${convertIntoHours * 60} Mins`;

                  setSyncDuration({
                    label: label,
                    total_in_minutes: selectedValue,
                  });
                }}
                labelFieldName={"label"}
                keyFieldName={"total_in_minutes"}
              />
            </Box>
          )}

          <FormGroup onChange={(e) => setDeleteBackupData(e.target.checked)}>
            <FormControlLabel
              control={<Checkbox checked={deleteBackupData} />}
              label="Delete backup data after 30 days from postgresql database?"
              sx={{ "& .MuiFormControlLabel-label": { fontSize: FS_BODY } }}
            />
          </FormGroup>

          <Box sx={{ flex: 1 }} />

          <Button
            onClick={() => handleSave()}
            autoFocus
            variant="contained"
            sx={{ minWidth: 150 }}
          >
            Save Settings
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default CustomDialog;
